<template>
    <div>
        <h1 class="font-bold text-4xl mb-20 flex flex-wrap gap-10 items-center">
            <span v-if="!workplace" class="emoji mr-5">☕️</span>
            <template v-if="workplace">
                <span>
                    <span class="emoji mr-5">☕️</span>
                    {{ workplace.name }}
                </span>

                <Tag v-if="workplace.recently_verified" class="font-normal text-sm shrink-0">
                    ✅ Recently verified
                </Tag>

                <Tag v-else-if="workplace.user_verifications.length > 0" class="font-normal text-sm shrink-0">
                    Verified
                </Tag>

                <Tag v-else-if="workplace.recently_added" class="font-normal text-sm shrink-0">
                    ✅ Recently added
                </Tag>
            </template>
        </h1>

        <p v-if="workplace" class="mb-20" v-html="workplace.description"></p>

        <div v-if="workplace" class="grid grid-cols-2 gap-10">
            <div class="details__grid">
                <div class="details__grid__row">
                    <div class="details__grid__title">City</div>
                    <div class="details__grid__content">
                        <router-link :to="`/cafes/${workplace.city.country.slug}/${workplace.city.slug}`"
                                     class="text-indigo-400 underline hover:text-pink-500 focus:text-pink-500">
                            <span class="emoji">{{ workplace.city.emoji }}</span> {{ workplace.city.name }}
                        </router-link>
                    </div>
                </div>
                <div class="details__grid__row">
                    <div class="details__grid__title">Type</div>
                    <div class="details__grid__content">
                        <span class="emoji">{{ workplace.type.emoji }}</span> {{ workplace.type.name }}
                    </div>
                </div>

                <div v-if="workplace.tags.length > 0" class="details__grid__row">
                    <div class="details__grid__title">Tags</div>
                    <div class="details__grid__content">
                        <div class="flex gap-5 mt-5 flex-wrap">
                            <Tag v-for="tag in workplace.tags" :key="tag.slug">
                                <span class="emoji">{{ tag.emoji }}</span> {{ tag.name }}
                            </Tag>
                        </div>
                    </div>
                </div>

                <div class="details__grid__row">
                    <div class="details__grid__title">Address</div>
                    <div class="details__grid__content">
                        <a :href="`https://www.google.com/maps/search/${encodeURIComponent(workplace.address)}`"
                           target="_blank">
                            {{ workplace.address }}
                        </a>
                    </div>
                </div>
                <div class="details__grid__row">
                    <div class="details__grid__title">Added</div>
                    <div class="details__grid__content">
                        {{ workplace.published_at_human_readable }}
                    </div>
                </div>

                <div class="details__grid__row">
                    <div class="details__grid__title">Added by</div>
                    <div class="details__grid__content">
                        <Component
                            :is="workplace.added_by_user?.twitter_username ? 'a' : 'span'"
                            :class="{ 'text-indigo-400 hover:text-pink-500 focus:text-pink-500': workplace.added_by_user?.twitter_username !== null }"
                            :href="workplace.added_by_user?.twitter_username ? `https://twitter.com/${workplace.added_by_user.twitter_username}` : null"
                            class="flex items-center"
                            rel="nofollow noopener"
                            target="_blank"
                        >
                            <img
                                :src="`${workplace.added_by_user?.public_avatar_url}&s=30`"
                                alt=""
                                class="inline-block rounded-full border-1 border-indigo-400 mr-5"
                                height="30"
                                width="30">
                            {{ workplace.added_by_user?.public_name }}
                        </Component>

                        <div v-if="workplace.credit_to_name" class="mt-5 text-sm text-gray-600">
                            with credit to
                            <span v-if="workplace.credit_to_url === null">
                                {{ workplace.credit_to_name }}
                            </span>
                            <a v-else
                               :href="workplace.credit_to_url"
                               class="text-indigo-400 hover:text-pink-500 focus:text-pink-500"
                               rel="nofollow noopener"
                               target="_blank">{{ workplace.credit_to_name }}</a>
                        </div>
                    </div>
                </div>

                <template v-if="showLastUpdated">
                    <div class="details__grid__row">
                        <div class="details__grid__title">Last updated</div>
                        <div class="details__grid__content">
                            {{ workplace.updated_at_human_readable }}
                        </div>
                    </div>
                </template>
            </div>
            <div class="">
                <div
                    :class="{ 'bg-gray-100': true }"
                    class="h-full relative"
                >
                    <l-map v-if="workplace"
                           ref="map"
                           v-model:zoom="zoom"
                           :center="[workplace.latitude, workplace.longitude]"
                           :max-zoom="19"
                           :options="{attributionControl: false}"
                           fade-animation>
                        <l-tile-layer
                            attribution="© <a target='_blank' href='https://www.openstreetmap.org/copyright'>OpenStreetMap</a> contributors"
                            layer-type="base"
                            name="OpenStreetMap"
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        ></l-tile-layer>

                        <l-control-attribution
                            position="bottomright"
                            prefix=""
                        />

                        <l-marker
                            v-if="workplace.latitude && workplace.longitude"
                            :lat-lng="[workplace.latitude, workplace.longitude]"
                            :name='workplace.name'
                            :options="{ interactive: false }"
                        >
                        </l-marker>
                    </l-map>
                </div>
            </div>
        </div>

        <div v-if="workplace" class="mt-50">
            <h1 class="font-bold text-2xl mb-20">
                <span class="emoji mr-5">👩🏼‍💻️</span>
                <template v-if="workplace">
                    User Verifications
                </template>
            </h1>

            <div v-if="workplace.user_verifications.length === 0"
                 class="rounded overflow-hidden border-1 border-gray-200 shadow bg-white p-15 max-w-2xl mx-auto">
                <p class="mb-10">
                    At the moment, we don't have any reviews and user verifications for
                    this {{ workplace.type.name.toLowerCase() }} yet. Be the first to share your experience with others
                    by verifying
                    this
                    workplace.
                </p>

                <p class="mb-10">
                    Your feedback is important to us and to other digital nomads, freelancers, and professionals who are
                    looking for the perfect spot to work and dine. With your help, we can create a comprehensive guide
                    to
                    the best laptop-friendly cafes in {{ workplace.city.name }}.
                </p>

                <p class="">Leaving a review is easy and only takes a few minutes.
                    Simply <a
                        :href="`mailto:max@maxkorlaar.com?subject=${encodeURI('DigitalNomadCafes.com verification for ' + workplace.name)}`"
                        class="text-indigo-400 underline hover:text-pink-500 focus:text-pink-500">add
                        a verification</a> and
                    share your thoughts on the cafe's atmosphere, Wi-Fi speed, power outlets, type of seating and where
                    they
                    allow laptops.
                    We value your input and appreciate your support in making Digital Nomad Cafes the go-to guide for
                    finding the best cafes to
                    work from in {{ workplace.city.name }}.
                </p>
            </div>

            <div v-else class="grid lg:grid-cols-2 grid-cols-1 md:grid-cols-2 mt-20 gap-15">
                <div v-for="verification in workplace.user_verifications"
                     class="rounded overflow-hidden border-1 border-gray-200 shadow bg-white p-15">
                    <p v-text="wrapInApostrophes(verification.remarks)"></p>

                    <p class="text-gray-600 mt-10">Verified by
                        <a
                            :href="`https://twitter.com/${verification.user.twitter_username}`"
                            class="text-indigo-400 hover:text-pink-500 focus:text-pink-500"
                            rel="nofollow noopener"
                            target="_blank"
                        >
                            <img
                                :src="`${verification.user.public_avatar_url}&s=30`"
                                alt=""
                                class="inline-block rounded-full border-1 border-indigo-400"
                                height="30"
                                width="30">
                            {{ verification.user.public_name }}
                        </a>,
                        {{ verification.verified_relative_ago }}
                    </p>
                </div>
            </div>
        </div>

        <div v-if="workplace" class="mt-50 text-gray-600">
            <p class="mb-10">
                When you're working on the go, it's important to have a good meal or a coffee to fuel your productivity.
                And when you're digital nomading, you have to put in some hours but also want to explore the city.
                Working from cafes is an ideal solution to getting work done and still discovering new places and
                getting around {{ workplace.city.name }}.
            </p>

            <p class="mb-10">
                Don't waste your time searching for the perfect spot when others have already done the work for you.
                Browse our list of top-rated
                <router-link :to="`/cafes/${workplace.city.country.slug}/${workplace.city.slug}`"
                             class="text-indigo-400 underline hover:text-pink-500 focus:text-pink-500">
                    laptop-friendly cafes in {{ workplace.city.name }}
                </router-link>
                and find the perfect
                place to
                work, relax, and connect.
            </p>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.details {
    &__grid {
        &__row {
            @apply grid py-10 even:bg-indigo-50 px-5 gap-10;
            align-items: center;
            grid-template-columns: minmax(max-content, 100px) 1fr;

            &:last-child {
                @apply border-b-0;
            }
        }

        &__title, &__content {
            //@apply py-10 ;
        }

        &__title {
            @apply font-bold pr-5;
        }

        &__content {
            //@apply border-b-1 border-b-indigo-300;
        }
    }
}
</style>

<script>
import {useMeta} from "vue-meta";
import {computed} from "vue";
import axios from "axios";
import {LControlAttribution, LMap, LMarker, LPopup, LTileLayer} from "@vue-leaflet/vue-leaflet";
import Tag from "../components/Tag.vue";

export default {
    name: "Workplace",
    components: {Tag, LMap, LTileLayer, LMarker, LPopup, LControlAttribution},
    props: {
        countrySlug: String,
        citySlug: String,
        workplaceSlug: String,
    },
    data() {
        return {
            zoom: 17,
            country: {},
            city: null,
            workplace: null,
            loading: false
        }
    },
    computed: {
        showLastUpdated() {
            return this.workplace?.updated_at_human_readable !== this.workplace?.published_at_human_readable;
        }
    },
    methods: {
        getWorkplace() {
            this.loading = true;

            axios.get(`/api/countries/${this.countrySlug}/cities/${this.citySlug}/workplaces/${this.workplaceSlug}`).then(response => {
                this.workplace = response.data;
            }).catch(error => {
                console.error(error);
                this.errors = error.response?.data?.errors ?? {};
            }).finally(() => {
                this.loading = false;
            });
        },
        wrapInApostrophes(text) {
            return `"${text}"`;
        }
    },
    created() {
        this.getWorkplace();
    },
    mounted() {
        useMeta(
            computed(() => {
                if (this.workplace) {
                    return {
                        title: this.workplace.meta.title,
                        description: this.workplace.meta.description,
                        og: {
                            title: `${this.workplace.name} in ${this.workplace.city.emoji} ${this.workplace.city.name}`,
                            image: `/api/countries/${this.workplace.city.country.slug}/cities/${this.workplace.city.slug}/workplaces/${this.workplaceSlug}/image`,
                            description: `${this.workplace.name}`
                        }
                    };
                }

                return {};
            })
        );
    },
}
</script>

<style scoped>

</style>
